import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'

const DURATION_MS = 5000

const CATEGORY_META = {
  Solar:      { color: '#b0ceae', icon: 'solar_power' },
  Klima:      { color: '#93c5fd', icon: 'ac_unit' },
  Fahrzeug:   { color: '#fcd34d', icon: 'directions_car' },
  Sicherheit: { color: '#ffb59b', icon: 'lock' },
  Person:     { color: '#ff6450', icon: 'person_search' },
  Tier:       { color: '#fcd34d', icon: 'pets' },
  Batterie:   { color: '#c4b5fd', icon: 'battery_charging_full' },
  System:     { color: '#b0ceae', icon: 'wb_twilight' },
  Energie:    { color: '#93c5fd', icon: 'bolt' },
  Haus:       { color: '#b0ceae', icon: 'blinds' },
}

export function EventToast({ event, onDismiss }) {
  const meta = CATEGORY_META[event.category] ?? CATEGORY_META['System']
  const hoveredRef   = useRef(false)
  const focusedRef   = useRef(document.hasFocus())
  const [paused, setPaused] = useState(!document.hasFocus())
  const timerRef     = useRef(null)
  const remainingRef = useRef(DURATION_MS)
  const startedAtRef = useRef(null)

  function shouldPause() { return hoveredRef.current || !focusedRef.current }

  function pause() {
    clearTimeout(timerRef.current)
    const elapsed = startedAtRef.current ? Date.now() - startedAtRef.current : 0
    remainingRef.current = Math.max(0, remainingRef.current - elapsed)
    startedAtRef.current = null
    setPaused(true)
  }

  function resume() {
    startedAtRef.current = Date.now()
    timerRef.current = setTimeout(onDismiss, remainingRef.current)
    setPaused(false)
  }

  useEffect(() => {
    if (!shouldPause()) resume()

    const onBlur  = () => { focusedRef.current = false; if (!shouldPause()) return; pause() }
    const onFocus = () => { focusedRef.current = true;  if (shouldPause()) return;  resume() }
    window.addEventListener('blur',  onBlur)
    window.addEventListener('focus', onFocus)
    return () => {
      clearTimeout(timerRef.current)
      window.removeEventListener('blur',  onBlur)
      window.removeEventListener('focus', onFocus)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleMouseEnter() {
    hoveredRef.current = true
    if (!paused) pause()
  }

  function handleMouseLeave() {
    hoveredRef.current = false
    if (!shouldPause()) resume()
  }

  const hasImage = !!event.image_url
  const width = hasImage ? 380 : 320

  return (
    <motion.div
      key={event.id}
      initial={{ opacity: 0, y: -16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0,   scale: 1 }}
      exit={{    opacity: 0, y: -12,  scale: 0.96 }}
      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'fixed',
        top: 76,
        right: 16,
        zIndex: 4000,
        width,
        background: 'rgba(27,28,27,0.97)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 14,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        overflow: 'hidden',
        fontFamily: "'Be Vietnam Pro', sans-serif",
      }}
    >
      {/* Image — full width, shown when present */}
      {hasImage && (
        <img
          src={event.image_url}
          alt=""
          style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
        />
      )}

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 14px 10px' }}>
        {/* Category chip */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: `${meta.color}22`,
          border: `1px solid ${meta.color}44`,
          borderRadius: 99, padding: '3px 8px 3px 6px',
          flex: '0 0 auto',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: '0.85rem', color: meta.color }}>
            {event.icon ?? meta.icon}
          </span>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: meta.color, letterSpacing: '0.02em' }}>
            {event.category}
          </span>
        </div>

        <div style={{ flex: 1 }} />

        {/* Close button */}
        <button
          onClick={onDismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(195,200,191,0.4)', padding: 2,
            display: 'flex', alignItems: 'center',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(195,200,191,0.8)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(195,200,191,0.4)'}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>close</span>
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '0 14px 14px' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(195,200,191,0.95)', marginBottom: 4, lineHeight: 1.3 }}>
          {event.title}
        </div>
        {event.description && (
          <div style={{ fontSize: '0.75rem', color: 'rgba(195,200,191,0.5)', lineHeight: 1.45 }}>
            {event.description}
          </div>
        )}
      </div>

      {/* Countdown bar */}
      <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', position: 'relative' }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: meta.color,
          transformOrigin: 'left center',
          animation: `toast-fill ${DURATION_MS}ms linear forwards`,
          animationPlayState: paused ? 'paused' : 'running',
        }} />
      </div>

      <style>{`
        @keyframes toast-fill {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </motion.div>
  )
}

export function EventToastPortal({ toast, onDismiss }) {
  return createPortal(
    <AnimatePresence>
      {toast && <EventToast key={toast.key} event={toast.event} onDismiss={onDismiss} />}
    </AnimatePresence>,
    document.body
  )
}
